import React, { useEffect, useRef, useState } from 'react';
import type { PendingDocument } from '@schemas/api';
import { useDocumentById } from '@api/hooks/useWorkflowStatus';
import { ConfidenceScore } from '@components/common/ConfidenceScore';
import { Button } from '@components/common/Button';
import { Card, CardContent } from '@components/common/Card';
import { DocumentReviewModal } from './DocumentReviewModal';
import { MatchStatusBadge } from './MatchStatusBadge';
import { Portal } from '@components/common/Portal';
import { formatCurrency, formatDate } from '@utils/formatters';
import { AlertCircle, AlertTriangle, ArrowDown, ArrowUp, ArrowUpDown, CheckCircle2, Flag } from 'lucide-react';

type ReviewStage = 'extraction' | 'approval_progress' | 'ap_posting' | 'payment';
type SortField = 'created_at' | 'vendor_name' | 'amount' | 'confidence' | 'erp_link';
type SortOrder = 'asc' | 'desc';

interface PendingDocumentsTableProps {
  documents: PendingDocument[];
  isLoading?: boolean;
  onReviewComplete?: () => void;
  initialDocumentId?: string | null;
  initialDocumentType?: PendingDocument['type'] | null;
  initialStage?: ReviewStage;
  onInitialDocumentClose?: () => void;
  sortBy?: SortField;
  sortOrder?: SortOrder;
  onSortChange?: (field: SortField) => void;
}

export const PendingDocumentsTable: React.FC<PendingDocumentsTableProps> = ({
  documents,
  isLoading = false,
  onReviewComplete,
  initialDocumentId = null,
  initialDocumentType = null,
  initialStage = 'extraction',
  onInitialDocumentClose,
  sortBy = 'created_at',
  sortOrder = 'desc',
  onSortChange,
}) => {
  const [selectedDocument, setSelectedDocument] = useState<PendingDocument | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const deepLinkedDocumentQuery = useDocumentById(initialDocumentId);
  const openedInitialDocumentRef = useRef<string | null>(null);

  const formatStatusLabel = (status?: string) => {
    if (!status) {
      return 'Draft';
    }

    return status
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const getStatusBadgeClass = (status?: string) => {
    switch (status) {
      case 'pending_extraction':
      case 'extracting':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'extracted':
      case 'pending_review':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'approved':
      case 'linked':
      case 'matched':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'orphaned':
      case 'unmatched':
      case 'hold':
      case 'split':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
      case 'rejected':
      case 'reject':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  const handleReviewClick = (doc: PendingDocument) => {
    setSelectedDocument(doc);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    const closingDocumentId = selectedDocument?.id;
    setIsModalOpen(false);
    setSelectedDocument(null);
    if (initialDocumentId && closingDocumentId === initialDocumentId) {
      openedInitialDocumentRef.current = null;
      onInitialDocumentClose?.();
    }
    onReviewComplete?.();
  };

  useEffect(() => {
    if (!initialDocumentId) {
      openedInitialDocumentRef.current = null;
      return;
    }

    if (openedInitialDocumentRef.current === initialDocumentId) {
      return;
    }

    if (isModalOpen && selectedDocument?.id !== initialDocumentId) {
      return;
    }

    const documentFromList = documents.find((doc) => doc.id === initialDocumentId);
    const documentFromQuery = deepLinkedDocumentQuery.data;
    const documentToOpen = documentFromList || documentFromQuery;

    if (!documentToOpen) {
      return;
    }

    if (initialDocumentType && documentToOpen.type !== initialDocumentType) {
      return;
    }

    setSelectedDocument(documentToOpen);
    setIsModalOpen(true);
    openedInitialDocumentRef.current = initialDocumentId;
  }, [
    deepLinkedDocumentQuery.data,
    documents,
    initialDocumentId,
    initialDocumentType,
    isModalOpen,
    selectedDocument?.id,
  ]);

  if ((isLoading || (initialDocumentId && deepLinkedDocumentQuery.isLoading)) && !selectedDocument) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <p className="text-gray-500 dark:text-gray-400">Loading documents...</p>
        </CardContent>
      </Card>
    );
  }

  if (documents.length === 0 && !selectedDocument) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <p className="text-gray-500 dark:text-gray-400">No pending documents</p>
        </CardContent>
      </Card>
    );
  }

  const SortableHeader = ({ field, label }: { field: SortField; label: string }) => {
    if (!onSortChange) {
      return (
        <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
          {label}
        </span>
      );
    }

    const isActive = sortBy === field;
    const Icon = isActive ? (sortOrder === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;

    return (
      <button
        type="button"
        onClick={() => onSortChange(field)}
        className="inline-flex items-center gap-1 text-left text-sm font-semibold text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100"
      >
        {label}
        <Icon className={`h-3.5 w-3.5 ${isActive ? '' : 'opacity-40'}`} />
      </button>
    );
  };

  return (
    <>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200 dark:border-gray-700">
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">
                Document #
              </th>
              <th className="px-4 py-3 text-left">
                <SortableHeader field="vendor_name" label="Vendor" />
              </th>
              <th className="px-4 py-3 text-left">
                <SortableHeader field="erp_link" label="ERP Link" />
              </th>
              <th className="px-4 py-3 text-left">
                <SortableHeader field="amount" label="Amount" />
              </th>
              <th className="px-4 py-3 text-left">
                <SortableHeader field="confidence" label="Confidence" />
              </th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">
                Status
              </th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">
                Issues
              </th>
              <th className="px-4 py-3 text-left">
                <SortableHeader field="created_at" label="Created" />
              </th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">
                Action
              </th>
            </tr>
          </thead>
          <tbody>
            {documents.map((doc) => {
              const missingRefs = doc.missing_references || [];
              const unresolved = missingRefs.filter((ref) => ref.status === 'unresolved');
              const unresolvedCritical = unresolved.filter((ref) => ref.is_critical);
              const activeFlags = (doc.document_flags || []).filter((f) => f.status !== 'resolved');
              const blockingFlags = activeFlags.filter((flag) =>
                flag.severity === 'critical' || flag.severity === 'error'
              );
              const blockingIssueCount = unresolvedCritical.length + blockingFlags.length;
              const hasBlockingIssues = Boolean(doc.has_blocking_issues || blockingIssueCount > 0);
              const statusValue = doc.status || doc.extraction_status || 'draft';
              const isOrphaned = statusValue === 'orphaned';

              return (
                <tr
                  key={doc.id}
                  className={`border-b border-gray-200 dark:border-gray-700 transition-colors ${
                    hasBlockingIssues
                      ? 'bg-red-50 dark:bg-red-950 hover:bg-red-100 dark:hover:bg-red-900'
                      : isOrphaned
                        ? 'bg-purple-50 dark:bg-purple-950 hover:bg-purple-100 dark:hover:bg-purple-900'
                        : unresolved.length > 0
                          ? 'bg-yellow-50 dark:bg-yellow-950 hover:bg-yellow-100 dark:hover:bg-yellow-900'
                          : 'hover:bg-gray-50 dark:hover:bg-gray-800'
                  }`}
                >
                  <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100">
                    {doc.external_number || `#${doc.id}`}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                    {doc.vendor_name || 'Unknown'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                    {doc.erp_connection_label || 'Unlinked'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                    {doc.total !== undefined && doc.total !== null
                      ? formatCurrency(doc.total, doc.currency || 'USD')
                      : '-'}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <ConfidenceScore score={doc.confidence} />
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <div className="flex flex-col items-start gap-1.5">
                      <span
                        className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${getStatusBadgeClass(
                          statusValue
                        )}`}
                      >
                        {formatStatusLabel(statusValue)}
                      </span>
                      <MatchStatusBadge document={doc} />
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <div className="flex flex-col gap-1">
                      {hasBlockingIssues ? (
                        <div className="flex items-center gap-2">
                          <AlertCircle className="h-4 w-4 text-red-600" />
                          <span className="text-red-600 font-medium">
                            {blockingIssueCount} blocking
                          </span>
                        </div>
                      ) : unresolved.length > 0 ? (
                        <div className="flex items-center gap-2">
                          <AlertTriangle className="h-4 w-4 text-yellow-600" />
                          <span className="text-yellow-600 font-medium">{unresolved.length} issue(s)</span>
                        </div>
                      ) : activeFlags.length === 0 ? (
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="h-4 w-4 text-green-600" />
                          <span className="text-green-600 text-xs">OK</span>
                        </div>
                      ) : null}
                      {activeFlags.length > 0 && (
                        <div className="flex items-center gap-2">
                          <Flag className="h-4 w-4 text-orange-500" />
                          <span className="text-orange-600 dark:text-orange-400 font-medium text-xs">
                            {activeFlags.length} flag{activeFlags.length !== 1 ? 's' : ''}
                          </span>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                    {formatDate(new Date(doc.created_at))}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <Button
                      size="sm"
                      variant="primary"
                      onClick={() => handleReviewClick(doc)}
                    >
                      Review
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {selectedDocument && (
        <Portal>
          <DocumentReviewModal
            isOpen={isModalOpen}
            document={selectedDocument}
            onClose={handleModalClose}
            initialStage={initialStage}
          />
        </Portal>
      )}
    </>
  );
};

export default PendingDocumentsTable;
